/**
 * Audio-preference persistence (D117, Mike's ask) — the Sound toggle and the
 * volume setting survive a reload, via `localStorage`.
 *
 * @remarks
 * App shell, the one place browser APIs belong — the store stays pure and
 * never knows where its defaults came from. The shape on disk is versioned
 * (`v1` in the key), every access is wrapped (private windows and locked-down
 * browsers throw on the ACCESSOR, not just the call), and every field is
 * validated on the way in: a hand-edited or stale value degrades to the
 * default rather than poisoning the slice. Absence is the default, exactly
 * as a fresh profile would be.
 *
 * The audio pair persists, and D153's clock preference joined it — same key,
 * same shape, one additive field, exactly as promised below — and D209's map
 * style the same way (the key still says `audio`, which is now a name rather
 * than a description; renaming it would orphan every stored volume). The
 * others (theme, text speed, images, reduced motion) join when their
 * controls exist.
 *
 * Deliberately NOT the save system: preferences are presentation, not world
 * state, so they live outside the R10 snapshot and survive `?seed=` (which
 * must always mean a fresh, reproducible SESSION — not a reset volume).
 */

import { mapStyleSchema } from '@orpheus/content-schema';
import {
	ambientAudioPreferenceSet,
	ambientAudioVolumeSet,
	type AutoWaitPreference,
	autoWaitPreferenceSet,
	type GameStore,
	mapStylePreferenceSet,
	selectAmbientAudioVolume,
	selectAutoWaitPreference,
	selectIsAmbientAudioEnabled,
	selectMapStylePreference,
} from '@orpheus/game-store';
import { type MapStylePreference } from '@orpheus/player-host';

/** The persisted audio preferences, validated. */
export interface StoredAudioPreferences {
	/** The volume control's SETTING (handle position), 0..1. */
	readonly ambientAudioVolume?: number;
	/** D153: the player's word on the pack's clock. */
	readonly autoWaitPreference?: AutoWaitPreference;
	/** Whether the ambience layer plays at all. */
	readonly isAmbientAudioEnabled?: boolean;
	/** D209: which of a pack's offered map styles the player looks at. */
	readonly mapStylePreference?: MapStylePreference;
}

/** The three words the clock preference may be — anything else on disk is a default. */
const AUTO_WAIT_PREFERENCES: readonly AutoWaitPreference[] = ['default', 'off', 'on'];

/** Whether a stored value is one of the clock preference's words. */
const isAutoWaitPreference = (value: unknown): value is AutoWaitPreference =>
	typeof value === 'string' && (AUTO_WAIT_PREFERENCES as readonly string[]).includes(value);

/**
 * The words the map style preference may be (D209) — the pack's opening map,
 * or a style by name. Derived from the schema's own list, never copied: a
 * copy here silently dropped `scene` on reload the day D210 added it, and a
 * new style must be remembered without this file hearing about it.
 */
const MAP_STYLE_PREFERENCES: readonly MapStylePreference[] = ['default', ...mapStyleSchema.options];

/** Whether a stored value is one of the map style preference's words. */
const isMapStylePreference = (value: unknown): value is MapStylePreference =>
	typeof value === 'string' && (MAP_STYLE_PREFERENCES as readonly string[]).includes(value);

/** The storage key — versioned so a future shape change is a new key, not a parse gamble. */
const STORAGE_KEY = 'orpheus.audio-preferences.v1';

/**
 * Read the persisted audio preferences, if any survive validation.
 *
 * @returns The validated fields (each absent when missing or malformed)
 */
export const readStoredAudioPreferences = (): StoredAudioPreferences => {
	try {
		const storedText = window.localStorage.getItem(STORAGE_KEY);

		if (storedText === null) {
			return {};
		}

		const parsedValue: unknown = JSON.parse(storedText);

		if (typeof parsedValue !== 'object' || parsedValue === null) {
			return {};
		}

		const candidate = parsedValue as Record<string, unknown>;
		const volume = candidate['ambientAudioVolume'];
		const isEnabled = candidate['isAmbientAudioEnabled'];
		const autoWait = candidate['autoWaitPreference'];
		const mapStyle = candidate['mapStylePreference'];

		return {
			...(typeof volume === 'number' && Number.isFinite(volume)
				? { ambientAudioVolume: Math.min(1, Math.max(0, volume)) }
				: {}),
			...(isAutoWaitPreference(autoWait) ? { autoWaitPreference: autoWait } : {}),
			...(typeof isEnabled === 'boolean' ? { isAmbientAudioEnabled: isEnabled } : {}),
			...(isMapStylePreference(mapStyle) ? { mapStylePreference: mapStyle } : {}),
		};
	} catch {
		// A browser refusing storage is a browser with default preferences.
		return {};
	}
};

/**
 * Persist the audio preferences. Failures are silent — persistence is a
 * convenience, and a private window must not turn the volume slider into an
 * error source.
 *
 * @param preferences - The current values to keep
 */
export const writeStoredAudioPreferences = (preferences: Required<StoredAudioPreferences>): void => {
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
	} catch {
		// Nothing to do: the session still works, the preference just won't
		// survive the reload.
	}
};

/**
 * Hydrate the store's audio preferences from storage, then keep storage
 * current — the whole persistence wiring, in one call at boot.
 *
 * @remarks
 * Hydration dispatches only the fields that survived validation, so a fresh
 * profile (or a refusing browser) leaves the slice's own defaults standing.
 * The write side subscribes to the store and persists on actual change — the
 * store stays the single source of truth and this module stays a mirror.
 *
 * @param store - The booted session's store
 */
export const connectAudioPreferencePersistence = (store: GameStore): void => {
	const storedPreferences = readStoredAudioPreferences();

	if (storedPreferences.ambientAudioVolume !== undefined) {
		store.dispatch(ambientAudioVolumeSet(storedPreferences.ambientAudioVolume));
	}

	if (storedPreferences.isAmbientAudioEnabled !== undefined) {
		store.dispatch(ambientAudioPreferenceSet(storedPreferences.isAmbientAudioEnabled));
	}

	if (storedPreferences.autoWaitPreference !== undefined) {
		store.dispatch(autoWaitPreferenceSet(storedPreferences.autoWaitPreference));
	}

	if (storedPreferences.mapStylePreference !== undefined) {
		store.dispatch(mapStylePreferenceSet(storedPreferences.mapStylePreference));
	}

	/** The persisted shape, read off the store. */
	const snapshotOf = (): Required<StoredAudioPreferences> => ({
		ambientAudioVolume: selectAmbientAudioVolume(store.getState()),
		autoWaitPreference: selectAutoWaitPreference(store.getState()),
		isAmbientAudioEnabled: selectIsAmbientAudioEnabled(store.getState()),
		mapStylePreference: selectMapStylePreference(store.getState()),
	});

	let persisted = snapshotOf();

	store.subscribe(() => {
		const current = snapshotOf();

		// Every committed turn ticks the store; only a PREFERENCE change is
		// worth a storage write.
		if (
			current.ambientAudioVolume === persisted.ambientAudioVolume &&
			current.autoWaitPreference === persisted.autoWaitPreference &&
			current.isAmbientAudioEnabled === persisted.isAmbientAudioEnabled &&
			current.mapStylePreference === persisted.mapStylePreference
		) {
			return;
		}

		persisted = current;
		writeStoredAudioPreferences(current);
	});
};
